import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import styled, { useTheme } from "styled-components";
import pencilImage from "@assets/images/auth/pencil.png";
import tabletImage from "@assets/images/auth/tablet.png";
import userImage from "@assets/images/auth/user-2.png";
import imacImage from "@assets/images/variant-jaangal/auth/imac.png";
import { Path } from "@core/routing/path";
import { useIsMobile } from "@utils/hooks";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { row, stack } from "@ui/components/layout";
import { SvgPath } from "@ui/components/shapes/svgPath";
import { desktop, mobile, scaledPx } from "@ui/styles/utils";

interface NotFoundPageProps extends React.HTMLAttributes<HTMLDivElement> {
	message?: string;
}
export const NotFoundPage: React.FC<NotFoundPageProps> = ({ message, ...htmlProps }) => {
	const theme = useTheme();
	const isMobile = useIsMobile();
	return (
		<Container {...htmlProps}>
			<LeftSide>
				<Title>
					<div>{I18n.t("404.oops")}</div>
					<div>{message ?? I18n.t("404.title")}</div>
				</Title>
				<BigTitle>404!</BigTitle>
				<Link to={Path.Root}>
					<PrimaryButton light>{I18n.t("404.back")}</PrimaryButton>
				</Link>
			</LeftSide>
			{!isMobile && (
				<RightSide>
					<Tablet src={tabletImage} />
					<Pen src={pencilImage} />
					<Line
						color={theme.colors.white}
						boxSize={[301, 341]}
						path={[
							[0, 132],
							[72, 0],
							[192, 330],
							[304, 282],
						]}
						points={[
							[45, 49],
							[151, 217],
						]}
					/>
					<Computer src={imacImage} />
					<User src={userImage} />
				</RightSide>
			)}
		</Container>
	);
};

const Container = styled.div`
	${row(0, "flex-start", "stretch")};
	height: 100vh;
	width: 100vw;
	position: fixed;
	left: 0;
	top: 0;

	@media ${desktop} {
		> * {
			width: 50%;
			flex-grow: 0;
			flex-shrink: 0;
		}
	}
`;

const LeftSide = styled.div`
	${stack(36, "center", "flex-start")}
	padding: 150px 50px 50px 150px;
	@media ${mobile} {
		${stack(20, "center", "center")}
		padding: 80px 20px 20px 20px;
		flex: 1;
	}
	background-color: ${({ theme }) => theme.colors.bodyBackground};
`;

const Title = styled.div`
	${({ theme }) => theme.textStyles.title};
	color: ${({ theme }) => theme.colors.secondary};
`;

const BigTitle = styled.div`
	color: ${({ theme }) => theme.colors.secondary};
	font-size: 8.125rem;
	font-weight: bold;
`;

const RightSide = styled.div`
	background-color: ${({ theme }) => theme.colors.secondary};
	position: relative;
`;
const Line = styled(SvgPath)`
	position: absolute;
	top: ${scaledPx(330)};
	right: 0;
	width: ${scaledPx(301)};
	height: ${scaledPx(341)};
	overflow: visible;
`;
const Computer = styled.img`
	position: absolute;
	top: ${scaledPx(165)};
	right: 0;
	width: ${scaledPx(565)};
`;
const Tablet = styled.img`
	position: absolute;
	top: ${scaledPx(425)};
	right: ${scaledPx(246)};
	width: ${scaledPx(222)};
`;
const Pen = styled.img`
	position: absolute;
	top: ${scaledPx(458)};
	right: ${scaledPx(196)};
	width: ${scaledPx(11)};
`;
const User = styled.img`
	position: absolute;
	top: ${scaledPx(628)};
	right: ${scaledPx(75)};
	width: ${scaledPx(65)};
`;
